import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createField, getExistingDatabase } from '@databases/utils/database/utils';

type JiraIssuePopoverDetailField = 'assignee' | 'priority' | 'status' | 'summary';
type JiraIssuePopoverFixtureVariant = 'single' | 'multi';

const JIRA_ISSUE_FIELD_ID = 'jira-issue-popover-issue-field';
const JIRA_CLOUD_ID = '0b480fdf-821b-49b9-9590-e373e15500e9';
const JIRA_ISSUE_POPOVER_DETAIL_FIELDS = [
  'priority',
  'assignee',
  'summary',
  'status'
] as const satisfies JiraIssuePopoverDetailField[];
const JIRA_ISSUE_POPOVER_ISSUE_COUNT = {
  single: 1,
  multi: 3
} as const satisfies Record<JiraIssuePopoverFixtureVariant, number>;

export const getDatabaseWithJiraIssuePopoverFields = ({
  contentId,
  variant
}: {
  contentId: string;
  variant: JiraIssuePopoverFixtureVariant;
}): Database<ObjectMode.EXISTING> => {
  const database = getExistingDatabase({ contentId });
  const issueCount = JIRA_ISSUE_POPOVER_ISSUE_COUNT[variant];
  const jiraIssueFieldBase = createField(DatabaseFieldType.JIRA_ISSUE, {
    id: JIRA_ISSUE_FIELD_ID,
    name: 'Jira issue field'
  });
  const jiraIssueField = {
    ...jiraIssueFieldBase,
    settings: {
      ...jiraIssueFieldBase.settings,
      cloudId: JIRA_CLOUD_ID,
      multi: issueCount > 1
    }
  };
  const jiraIssueDetailsFields = JIRA_ISSUE_POPOVER_DETAIL_FIELDS.map(
    (jiraField, index) =>
      createField(DatabaseFieldType.JIRA_ISSUE_DETAILS, {
        id: `jira-issue-popover-${jiraField}-details-field`,
        name: `Jira issue details ${index + 1}`
      })
  );
  const fields: DatabaseField[] = [jiraIssueField, ...jiraIssueDetailsFields];
  const firstEntryId = database.entries[0];
  const jiraIssues = Array.from({ length: issueCount }, (_, index) => ({
    _id: `${index}`,
    id: `${index}`,
    key: `ISSUE-${index + 1}`
  }));

  return {
    ...database,
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    entriesById: {
      ...database.entriesById,
      [firstEntryId]: {
        ...database.entriesById[firstEntryId],
        values: {
          ...database.entriesById[firstEntryId].values,
          [jiraIssueField.id]: {
            type: DatabaseFieldType.JIRA_ISSUE,
            value: jiraIssues
          },
          ...Object.fromEntries(
            jiraIssueDetailsFields.map((jiraIssueDetailsField) => [
              jiraIssueDetailsField.id,
              {
                type: DatabaseFieldType.JIRA_ISSUE_DETAILS,
                value: undefined
              }
            ])
          )
        } as Record<string, DatabaseValue<DatabaseFieldType, object>>
      }
    }
  };
};
